import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { Box } from "@mui/material"
import { grey } from "@mui/material/colors"
import GridCell from "./GridCell"
import FooterTextField from "./FooterTextField"
import { useModalPartida } from "hooks/modalPartida/useModalPartida"
import i18n from "i18n"
import { checkIfMeasuresAreEmpty } from "utils/functions"
import UnityDropdownMUI from "./UnityDropdownMUI"
import QuantityInputWithToggle from "./QuantityInputWithToggle"

const LeftFooterGrid = ({
  unity,
  currency,
  setUnity,
  toggleBenefits,
  setToggleBenefits,
  setEnableEdit,
  enableEdit,
  bancoDePrecios,
  mediciones,
  totalDeTotales,
  details,
}) => {
  const {
    state,
    dispatchQuantity,
    setQuantityAndRecalculate,
    dispatchCost,
    setCostAndRecalculate,
    dispatchTotalCost,
    setTotalCostAndRecalculate,
    dispatchCostReduction,
    setCostReductionAndRecalculate,
  } = useModalPartida()

  const handleToggleClick = () => {
    setToggleBenefits(prev => !prev)
    setEnableEdit(true)
  }

  const isReadOnlyUnity = bancoDePrecios === "BANCO DE PRECIOS REFERENCIA"

  const shouldShowTotal = useMemo(() => {
    return mediciones.length > 0 && !checkIfMeasuresAreEmpty(mediciones)
  }, [mediciones])

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gridTemplateRows: "1fr 2fr 2fr",
        width: "100%",
        gap: "4px",
        paddingLeft: "4px",
        paddingRight: "2px",
        paddingBlock: "4px",
        backgroundColor: "#d9d9d9",
        borderBottomLeftRadius: "10px",
      }}
    >
      <GridCell col="1 / 3" row="1 / 2" />
      <GridCell col="3 / 5" row="1 / 2">
        {i18n.t("footerGrid.targetCost")}
      </GridCell>

      <GridCell col="1 / 2" row="2">
        <GridCell col="1 / 2" row="2">
          <QuantityInputWithToggle
            toggleBenefits={toggleBenefits}
            handleToggleClick={handleToggleClick}
            value={
              shouldShowTotal && enableEdit
                ? totalDeTotales
                : state.leftGridContent.quantity
            }
            disabled={mediciones.length > 0}
            onChange={dispatchQuantity}
            onBlur={setQuantityAndRecalculate}
            label={i18n.t("footerGrid.measurements")}
          />
        </GridCell>
      </GridCell>

      <GridCell col="2 / 3" row="2">
        <UnityDropdownMUI
          label={i18n.t("footerGrid.unit")}
          unity={unity}
          setUnity={setUnity}
          disabled={isReadOnlyUnity}
        />
      </GridCell>

      <GridCell col="3 / 4" row="2">
        <FooterTextField
          label={i18n.t("footerGrid.unitCost")}
          value={state.leftGridContent.cost}
          onChange={dispatchCost}
          onBlur={value => setCostAndRecalculate(value)}
          disabled={isReadOnlyUnity || details.length > 0}
          suffix={currency}
        />
      </GridCell>

      <GridCell col="4 / 5" row="2">
        <FooterTextField
          label={i18n.t("footerGrid.totalCost")}
          value={state.leftGridContent.totalCost}
          onChange={dispatchTotalCost}
          onBlur={value => setTotalCostAndRecalculate(value)}
          disabled={!!bancoDePrecios || details.length > 0}
          suffix={currency}
        />
      </GridCell>

      <GridCell col="1 / 2" row="3">
        <FooterTextField
          label={i18n.t("footerGrid.costReductionPercent")}
          value={state.leftGridContent.costReduction}
          onChange={dispatchCostReduction}
          onBlur={value => setCostReductionAndRecalculate(value)}
          suffix="%"
          sx={{ borderBottomLeftRadius: "10px" }}
        />
      </GridCell>

      <GridCell col="2 / 3" row="3">
        <FooterTextField
          label={i18n.t("footerGrid.decrease")}
          value={state.leftGridContent.euroReduced}
          disabled
          suffix={currency}
        />
      </GridCell>

      <GridCell col="3 / 4" row="3">
        <FooterTextField
          label={i18n.t("footerGrid.reducedUnitPrice")}
          value={state.leftGridContent.priceReduced}
          suffix={currency}
          disabled
        />
      </GridCell>

      <GridCell col="4 / 5" row="3">
        <FooterTextField
          label={i18n.t("footerGrid.reducedTotal")}
          disabled
          value={state.leftGridContent.totalReduced}
          suffix={currency}
        />
      </GridCell>
    </Box>
  )
}

LeftFooterGrid.propTypes = {
  unity: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  setUnity: PropTypes.func.isRequired,
  toggleBenefits: PropTypes.bool.isRequired,
  setToggleBenefits: PropTypes.func.isRequired,
  enableEdit: PropTypes.bool.isRequired,
  setEnableEdit: PropTypes.func.isRequired,
  bancoDePrecios: PropTypes.string,
  mediciones: PropTypes.array.isRequired,
  details: PropTypes.array.isRequired,
  totalDeTotales: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

LeftFooterGrid.defaultProps = {
  mediciones: [],
  enableEdit: false,
}

export default LeftFooterGrid
